import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ThumbsUp, ThumbsDown, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAgentLearning } from '@/hooks/useAgentLearning';

interface FeedbackButtonsProps {
  interactionId: string | null;
  className?: string;
  size?: 'sm' | 'md';
}

export const FeedbackButtons: React.FC<FeedbackButtonsProps> = ({
  interactionId,
  className,
  size = 'sm',
}) => {
  const { thumbsUp, thumbsDown, getFeedbackState, isSubmitting } = useAgentLearning();

  if (!interactionId) return null;

  const feedbackState = getFeedbackState(interactionId);

  // Already submitted
  if (feedbackState?.submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className={cn(
          'flex items-center gap-1.5 text-muted-foreground',
          className
        )}
      >
        <Check size={size === 'sm' ? 12 : 14} className="text-green-500" />
        <span className={cn('text-xs', size === 'sm' && 'text-[10px]')}>
          Thanks!
        </span>
      </motion.div>
    );
  }

  const buttonSize = size === 'sm' ? 'h-6 w-6' : 'h-7 w-7';
  const iconSize = size === 'sm' ? 12 : 14;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        className={cn('flex items-center gap-1', className)}
      >
        <button
          onClick={() => thumbsUp(interactionId)}
          disabled={isSubmitting}
          className={cn(
            'rounded-full flex items-center justify-center transition-all',
            'text-muted-foreground hover:text-green-500 hover:bg-green-500/10',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            buttonSize
          )}
          title="Helpful"
        >
          <ThumbsUp size={iconSize} />
        </button>
        <button
          onClick={() => thumbsDown(interactionId)}
          disabled={isSubmitting}
          className={cn(
            'rounded-full flex items-center justify-center transition-all',
            'text-muted-foreground hover:text-red-500 hover:bg-red-500/10',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            buttonSize
          )}
          title="Not helpful"
        >
          <ThumbsDown size={iconSize} />
        </button>
      </motion.div>
    </AnimatePresence>
  );
};
